import { ReactNode, useEffect, useMemo, useState } from 'react';

import { Box, Tab, Tabs } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Card from '@/components/card';
import { useCreatorPostVisuals } from '@/contexts/CreatorDetailsContext';
import { Posts } from '@/models/posts';

import CreatorPostChildren from './CreatorPostChildren';
import CreatorPostOcr from './CreatorPostOcr';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`visuals-tabpanel-${index}`}
    aria-labelledby={`visuals-tab-${index}`}
    {...other}
  >
    {value === index && <Box paddingY={2}>{children}</Box>}
  </div>
);

const getTabStyles = (theme: any, selectedValue: number, tabValue: number) => ({
  textTransform: 'none',
  fontWeight: selectedValue === tabValue ? 'bold' : 'normal',
  minWidth: '120px',
  '&:hover': { color: 'inherit', backgroundColor: theme.palette.grey[200] },
  '&.Mui-selected': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.grey[100]
  }
});

interface CreatorPostVisualsProps {
  media: Posts.Media[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  resetMarkers?: () => void;
  setVideoPlaybackStart?: (seconds: number) => void;
  setVideoLastClick?: (ts: number) => void;
}

const CreatorPostVisuals = ({
  media,
  activeIndex,
  setActiveIndex,
  resetMarkers,
  setVideoPlaybackStart,
  setVideoLastClick
}: CreatorPostVisualsProps) => {
  const theme = useTheme();
  const { clearSelections } = useCreatorPostVisuals();

  const hasMinorsResults = useMemo(
    () =>
      media.some((m) => m.visualRecognition?.some((r) => r.type === 'minors')),
    [media]
  );

  const hasOcrAdvancedItems = useMemo(
    () =>
      media.some((m) =>
        m.visualRecognition?.some(
          (r) => r.type === 'ocr-adv' && r.items?.length > 0
        )
      ),
    [media]
  );

  const hasOcrOldItems = useMemo(
    () =>
      media.some((m) =>
        m.visualRecognition?.some(
          (r) => r.type === 'ocr' && r.items?.length > 0
        )
      ),
    [media]
  );

  const hasOcrResults = hasOcrAdvancedItems || hasOcrOldItems;

  const [activeTab, setActiveTab] = useState(() => {
    if (hasOcrAdvancedItems) return 1;
    if (!hasMinorsResults && hasOcrOldItems) return 1;
    return 0;
  });

  useEffect(() => {
    if (hasOcrAdvancedItems) {
      setActiveTab(1);
    } else if (!hasMinorsResults && hasOcrOldItems) {
      setActiveTab(1);
    } else if (hasMinorsResults && !hasOcrResults) {
      setActiveTab(0);
    }
  }, [hasMinorsResults, hasOcrOldItems, hasOcrAdvancedItems, hasOcrResults]);

  const handleTabChange = (_: React.SyntheticEvent, newTab: number) => {
    setActiveTab(newTab);
    clearSelections();
    resetMarkers?.();
  };

  if (!hasMinorsResults && !hasOcrResults) return null;

  return (
    <Card
      sx={{ marginBottom: 2 }}
      isCollapsible
      title="Visuals"
      content={
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTabs-indicator': { height: '4px' }
            }}
          >
            {hasMinorsResults && (
              <Tab
                label="Children"
                value={0}
                sx={getTabStyles(theme, activeTab, 0)}
              />
            )}
            {hasOcrResults && (
              <Tab
                label="Text on screen"
                value={1}
                sx={getTabStyles(theme, activeTab, 1)}
              />
            )}
          </Tabs>
          <TabPanel value={activeTab} index={0}>
            <CreatorPostChildren
              media={media}
              setVideoPlaybackStart={setVideoPlaybackStart}
              setVideoLastClick={setVideoLastClick}
            />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <CreatorPostOcr
              media={media}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
              setVideoPlaybackStart={setVideoPlaybackStart}
              setVideoLastClick={setVideoLastClick}
            />
          </TabPanel>
        </>
      }
    />
  );
};

export default CreatorPostVisuals;
